/**
 * usePhotoUpload Hook
 *
 * Custom hook for uploading photos to Firebase Storage.
 * Handles upload progress, error states, and returns download URLs.
 */

import { useState, useCallback } from 'react';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTaskSnapshot,
} from 'firebase/storage';
import { getFirebaseStorage } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';

/**
 * Maximum file size in bytes (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Allowed MIME types for photos
 */
export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Single photo upload progress state
 */
export interface PhotoUploadProgress {
  /** File being uploaded */
  file: File;
  /** Upload progress (0-100) */
  progress: number;
  /** Whether upload is complete */
  completed: boolean;
  /** Download URL if completed */
  url?: string;
  /** Error message if failed */
  error?: string;
}

/**
 * Photo upload result
 */
export interface PhotoUploadResult {
  /** Original file */
  file: File;
  /** Firebase Storage download URL */
  url: string;
  /** Storage path for the file */
  storagePath: string;
}

/**
 * Upload state interface
 */
interface UsePhotoUploadState {
  /** Whether any upload is in progress */
  uploading: boolean;
  /** Progress for each file being uploaded */
  progressList: PhotoUploadProgress[];
  /** Overall error message */
  error: string | null;
}

/**
 * Return type for usePhotoUpload hook
 */
interface UsePhotoUploadResult extends UsePhotoUploadState {
  /**
   * Upload multiple photos to Firebase Storage
   *
   * @param files - Array of files to upload
   * @param routeId - Route ID for storage path
   * @returns Array of upload results with URLs
   */
  uploadPhotos: (files: File[], routeId: string) => Promise<PhotoUploadResult[]>;

  /**
   * Delete a photo from Firebase Storage
   *
   * @param storagePath - Storage path of the file to delete
   */
  deletePhoto: (storagePath: string) => Promise<void>;

  /**
   * Validate files before upload
   *
   * @param files - Files to validate
   * @returns Validation result with errors if any
   */
  validateFiles: (files: File[]) => { valid: boolean; errors: string[] };

  /**
   * Reset upload state
   */
  reset: () => void;
}

/**
 * Generate a unique filename for uploaded photo
 *
 * @param originalName - Original file name
 * @param index - Photo index (0, 1, 2)
 * @returns Unique filename
 */
const generateFilename = (originalName: string, index: number): string => {
  const extension = originalName.split('.').pop()?.toLowerCase() ?? 'jpg';
  const timestamp = Date.now();
  return `photo_${index}_${timestamp}.${extension}`;
};

/**
 * Hook to upload photos to Firebase Storage
 *
 * Features:
 * - Upload multiple photos with progress tracking
 * - File validation (size, type)
 * - Error handling
 * - Delete functionality
 *
 * @example
 * ```tsx
 * const { uploadPhotos, uploading, progressList, error } = usePhotoUpload();
 *
 * const handleUpload = async (files: File[]) => {
 *   const results = await uploadPhotos(files, routeId);
 *   // results[0].url contains the download URL
 * };
 * ```
 */
export const usePhotoUpload = (): UsePhotoUploadResult => {
  const { user } = useAuth();
  const [state, setState] = useState<UsePhotoUploadState>({
    uploading: false,
    progressList: [],
    error: null,
  });

  /**
   * Validate files before upload
   */
  const validateFiles = useCallback((files: File[]): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check number of files
    if (files.length === 0) {
      errors.push('At least one photo is required');
    }
    if (files.length > 3) {
      errors.push('Maximum 3 photos allowed');
    }

    // Check each file
    files.forEach((file, index) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`Photo ${index + 1} exceeds 10MB limit`);
      }

      // Check file type
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        errors.push(`Photo ${index + 1} must be JPEG, PNG, or WebP`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }, []);

  /**
   * Upload a single file to Firebase Storage
   */
  const uploadSingleFile = useCallback(
    async (
      file: File,
      routeId: string,
      index: number,
      onProgress: (progress: number) => void
    ): Promise<PhotoUploadResult> => {
      const storage = getFirebaseStorage();
      const filename = generateFilename(file.name, index);
      const storagePath = `routes/${routeId}/${filename}`;
      const storageRef = ref(storage, storagePath);

      return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file, {
          contentType: file.type,
          customMetadata: {
            uploadedBy: user?.uid ?? 'unknown',
            originalName: file.name,
          },
        });

        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            onProgress(progress);
          },
          (error) => {
            reject(new Error(`Upload failed: ${error.message}`));
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({ file, url, storagePath });
            } catch (error) {
              reject(
                new Error(
                  `Failed to get download URL: ${error instanceof Error ? error.message : 'Unknown error'}`
                )
              );
            }
          }
        );
      });
    },
    [user]
  );

  /**
   * Upload multiple photos to Firebase Storage
   */
  const uploadPhotos = useCallback(
    async (files: File[], routeId: string): Promise<PhotoUploadResult[]> => {
      // Validate files
      const validation = validateFiles(files);
      if (!validation.valid) {
        throw new Error(validation.errors.join('. '));
      }

      // Check authentication
      if (!user) {
        throw new Error('Must be logged in to upload photos');
      }

      // Initialize progress tracking
      const initialProgress: PhotoUploadProgress[] = files.map((file) => ({
        file,
        progress: 0,
        completed: false,
      }));

      setState({
        uploading: true,
        progressList: initialProgress,
        error: null,
      });

      try {
        // Upload all files in parallel
        const uploadPromises = files.map((file, index) =>
          uploadSingleFile(file, routeId, index, (progress) => {
            setState((prev) => ({
              ...prev,
              progressList: prev.progressList.map((p, i) => (i === index ? { ...p, progress } : p)),
            }));
          }).then((result) => {
            // Mark as completed
            setState((prev) => ({
              ...prev,
              progressList: prev.progressList.map((p, i) =>
                i === index ? { ...p, completed: true, url: result.url } : p
              ),
            }));
            return result;
          })
        );

        const results = await Promise.all(uploadPromises);

        setState((prev) => ({
          ...prev,
          uploading: false,
        }));

        return results;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';

        setState((prev) => ({
          ...prev,
          uploading: false,
          error: errorMessage,
        }));

        throw error;
      }
    },
    [user, validateFiles, uploadSingleFile]
  );

  /**
   * Delete a photo from Firebase Storage
   */
  const deletePhoto = useCallback(
    async (storagePath: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to delete photos');
      }

      const storage = getFirebaseStorage();
      const storageRef = ref(storage, storagePath);

      try {
        await deleteObject(storageRef);
      } catch (error) {
        // Ignore if file doesn't exist
        const errorMessage = error instanceof Error ? error.message : '';
        if (!errorMessage.includes('object-not-found')) {
          throw error;
        }
      }
    },
    [user]
  );

  /**
   * Reset upload state
   */
  const reset = useCallback(() => {
    setState({
      uploading: false,
      progressList: [],
      error: null,
    });
  }, []);

  return {
    ...state,
    uploadPhotos,
    deletePhoto,
    validateFiles,
    reset,
  };
};
